// src/components/ExcelUploader.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react'
import FileUploadArea from './FileUploadArea'
import ProcessingStatus from './ProcessingStatus'
import SettingsTab, { getUploadTypeConfig } from './SettingsTab'
import AuthorityAnalysis from './AuthorityAnalysis'
import { useLocation, useHistory } from 'react-router-dom'
import StudentAnalysis from './StudentAnalysis'
import { ReactComponent as GoNextIcon } from '../../../assets/svg/GoNextIcon.svg'
// import { processExcelFile } from '../../../utils/excelProcessor';
import { organizeClassTree, detectStudentColumns } from '../../../utils/classNameProcessor'
import { analyzeStaffAuthority } from '../../../utils/authorityAnalyzer'
import { analyzeStudentData } from '../../../utils/studentAnalyzer'

import UploadTypeSelection from './UploadTypeSelection'
import { processExcelFile } from '../../../utils/excelProcessor'
import { getCurrentClassesAndStaff, getImportStatus, sendDataForUploading, getUploadsHistory, uploadFileToServer } from '../../../server/server'
import { useTranslation } from 'react-i18next'
import PreviousUploadsModal from '../../modals/PreviousUploadsModal'
import TemplateSection from './TemplateSection'
import { ANALYTICS_EVENTS } from '../../../analytics/events'
import useTrackedSurface from '../../../analytics/useTrackedSurface'

const getStepFromSearch = (search) => {
    const step = new URLSearchParams(search).get('step')
    switch (step) {
        case 'upload_type':
            return 0
        case 'upload_file':
            return 1
        case 'settings':
            return 2
        case 'preview':
            return 3
        default:
            return 0
    }
}

const setSearchFromStep = (step) => {
    switch (step) {
        case 0:
            return 'upload_type'
        case 1:
            return 'upload_file'
        case 2:
            return 'settings'
        case 3:
            return 'preview'
        default:
            return 'upload_type'
    }
}

function useExcelProcessing() {
    const [progress, setProgress] = useState(0)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState(null)
    const [sheetNames, setSheetNames] = useState([])
    const [studentsData, setStudentsData] = useState([])
    const [staffData, setStaffData] = useState([])
    const [result, setResult] = useState(null)

    const processFile = useCallback(async (file) => {
        setProgress(0)
        setIsLoading(true)
        setError(null)
        setSheetNames([])
        // setStudentsData([]);
        // setStaffData([]);
        setResult(null)
        try {
            const result = await processExcelFile(file, (p) => setProgress(p))
            console.log(`✅ Processed file: ${file.name} with ${result.processedSheets.length} sheets`)
            console.log(`✅ Found ${result.studentsData.length} student records and ${result.staffData.length} staff records`)
            setSheetNames(result.processedSheets)
            setStudentsData(result?.studentsData ?? [])
            setStaffData(result?.staffData ?? [])
            setResult(result)
        } catch (err) {
            console.error('Error processing file:', err)
            setError(err.message || 'Failed to process file')
        } finally {
            setIsLoading(false)
            setTimeout(() => setProgress(0), 500)
        }
    }, [])

    const reset = () => {
        console.log('Resetting processing state')
        setProgress(0)
        setIsLoading(false)
        setError(null)
        setSheetNames([])
        setStudentsData([])
        setStaffData([])
        setResult(null)
    }

    return {
        progress,
        isLoading,
        error,
        sheetNames,
        studentsData,
        staffData,
        result,
        processFile,
        reset,
    }
}

function ExcelUploader() {
    const history = useHistory()
    const location = useLocation()
    const prevStep = useRef(null)
    const [currentStep, setCurrentStep] = useState(0)
    const prevStepRef = useRef(currentStep)
    const [uploadType, setUploadType] = useState(null)
    const [isProcessingFile, setIsProcessingFile] = useState(false)
    const [processingProgress, setProcessingProgress] = useState(0)
    const [studentAnalysis, setStudentAnalysis] = useState(null)
    const [studentsData, setStudentsData] = useState([])
    const [staffData, setStaffData] = useState([])
    const [sheetNames, setSheetNames] = useState([])
    const [error, setError] = useState(null)
    const [validationErrors, setValidationErrors] = useState([])
    const [activeTab, setActiveTab] = useState('students') // 'students' or 'staff'
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [submitSuccess, setSubmitSuccess] = useState(null)
    const [submitError, setSubmitError] = useState(null)
    const [classTree, setClassTree] = useState({})
    const [isImporting, setIsImporting] = useState(false)
    const [authorityAnalysis, setAuthorityAnalysis] = useState(null)
    const [settings, setSettings] = useState({
        removePreviousData: true,
        promptNewPassword: false,
        useSheetPassword: false,
        savePasswordsToMasterlist: false,
        uploadType: null,
    })
    const [currentClasses, setCurrentClasses] = useState([])
    const [currentStaff, setCurrentStaff] = useState([])
    const [uploadedFile, setUploadedFile] = useState(null)
    const [showPreviousUploadsModal, setShowPreviousUploadsModal] = useState(false)
    const [pdfFile, setPdfFile] = useState(null)
    const [isUploadingPdf, setIsUploadingPdf] = useState(false)
    const [pdfUploadError, setPdfUploadError] = useState(null)
    const [uploadedPdfPath, setUploadedPdfPath] = useState(null)
    const { t, locale } = useTranslation()
    const stepEvents = {
        0: ANALYTICS_EVENTS.MANAGEMENT_UPLOAD_ENGINE_TYPE_VIEWED,
        1: ANALYTICS_EVENTS.MANAGEMENT_UPLOAD_ENGINE_FILE_VIEWED,
        3: ANALYTICS_EVENTS.MANAGEMENT_UPLOAD_ENGINE_PREVIEW_VIEWED,
    }
    const uploadedFileName = uploadedFile?.name
    useTrackedSurface(stepEvents[currentStep], { contentId: uploadedFileName, content: uploadedFileName })
    useTrackedSurface(ANALYTICS_EVENTS.MANAGEMENT_UPLOAD_ENGINE_VALIDATION_VIEWED, {
        active: currentStep === 3 && validationErrors.length > 0,
        contentId: uploadedFileName,
        content: uploadedFileName,
    })
    useTrackedSurface(ANALYTICS_EVENTS.MANAGEMENT_UPLOAD_ENGINE_ANALYSIS_VIEWED, {
        active: currentStep === 3 && (!!studentAnalysis || !!authorityAnalysis),
        contentId: uploadedFileName,
        content: uploadedFileName,
    })
    useTrackedSurface(ANALYTICS_EVENTS.MANAGEMENT_UPLOAD_ENGINE_STATUS_VIEWED, {
        active: isProcessingFile || isSubmitting || isImporting || !!submitSuccess || !!submitError,
        contentId: uploadedFileName,
        content: uploadedFileName,
    })

    // Add ref for scrolling to top
    const containerRef = useRef(null)

    const {
        progress: excelProgress,
        isLoading: excelIsLoading,
        error: excelError,
        sheetNames: excelSheetNames,
        studentsData: excelStudentsData,
        staffData: excelStaffData,
        processFile,
        reset: resetProcessExcel,
    } = useExcelProcessing()

    useEffect(() => {
        getCurrentClassesAndStaffFromServer()
    }, [])

    useEffect(() => {
        if (!new URLSearchParams(location.search).has('step')) {
            history.replace({ pathname: location.pathname, search: `?step=${setSearchFromStep(currentStep)}` })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const getCurrentClassesAndStaffFromServer = async () => {
        const resp = await getCurrentClassesAndStaff()
        if (resp.status === 200) {
            const { classes, staff } = resp.data
            setCurrentClasses(classes)
            setCurrentStaff(staff)
        }
    }

    useEffect(() => {
        if (excelStudentsData && excelStudentsData.length > 0) {
            setStudentsData(excelStudentsData)
            setStaffData(excelStaffData)
            const analysis = analyzeStudentData(excelStudentsData)
            setStudentAnalysis(analysis)
            if (analysis.issues && analysis.issues.length > 0) {
                const studentValidationErrors = analysis.issues.filter((issue) => issue.type === 'error').map((issue) => issue.message)
                if (studentValidationErrors.length > 0) {
                    setValidationErrors((prev) => [...prev, ...studentValidationErrors])
                }
            }
        } else {
            setStudentAnalysis(null)
        }
    }, [excelStudentsData])

    useEffect(() => {
        if (uploadType) {
            setSettings((prev) => ({
                ...prev,
                uploadType: uploadType,
                removePreviousData: uploadType === 'full_update', // Auto-set based on upload type
            }))
        }
    }, [uploadType])

    // Staff validation function
    const validateStaffData = (staffData) => {
        if (!staffData || !staffData.length) {
            return {
                isValid: true,
                issues: [],
                summary: {
                    total: 0,
                    validRecords: 0,
                    missingEmails: 0,
                    missingFirstNames: 0,
                    missingLastNames: 0,
                    duplicateEmails: 0,
                },
            }
        }

        const issues = []
        const emails = new Map()
        let validRecords = 0
        let missingEmails = 0
        let missingFirstNames = 0
        let missingLastNames = 0
        let duplicateEmails = 0

        // Find required columns
        const findEmailColumn = (staffSample) => {
            if (!staffSample) return null
            const possibleEmailColumns = ['email', 'Email', 'EMAIL', 'e-mail', 'E-mail', 'username', 'Username', 'user name', 'User Name', 'mail', 'Mail']

            // First try exact matches
            for (const column of possibleEmailColumns) {
                if (staffSample.hasOwnProperty(column)) {
                    return column
                }
            }

            // Then try partial matches
            const columns = Object.keys(staffSample)
            for (const column of columns) {
                const lowerColumn = column.toLowerCase()
                if (lowerColumn.includes('email') || lowerColumn.includes('mail') || (lowerColumn.includes('user') && lowerColumn.includes('name'))) {
                    return column
                }
            }
            return null
        }

        const findFirstNameColumn = (staffSample) => {
            if (!staffSample) return null
            const possibleFirstNameColumns = ['first name', 'First Name', 'firstname', 'FirstName', 'FIRST NAME', 'fname', 'Fname', 'given name', 'Given Name']

            // First try exact matches
            for (const column of possibleFirstNameColumns) {
                if (staffSample.hasOwnProperty(column)) {
                    return column
                }
            }

            // Then try partial matches
            const columns = Object.keys(staffSample)
            for (const column of columns) {
                const lowerColumn = column.toLowerCase()
                if (
                    (lowerColumn.includes('first') && lowerColumn.includes('name')) ||
                    lowerColumn === 'firstname' ||
                    lowerColumn === 'fname' ||
                    (lowerColumn.includes('given') && lowerColumn.includes('name'))
                ) {
                    return column
                }
            }
            return null
        }

        const findLastNameColumn = (staffSample) => {
            if (!staffSample) return null
            const possibleLastNameColumns = ['last name', 'Last Name', 'lastname', 'LastName', 'LAST NAME', 'lname', 'Lname', 'surname', 'Surname', 'family name', 'Family Name']

            // First try exact matches
            for (const column of possibleLastNameColumns) {
                if (staffSample.hasOwnProperty(column)) {
                    return column
                }
            }

            // Then try partial matches
            const columns = Object.keys(staffSample)
            for (const column of columns) {
                const lowerColumn = column.toLowerCase()
                if (
                    (lowerColumn.includes('last') && lowerColumn.includes('name')) ||
                    lowerColumn === 'lastname' ||
                    lowerColumn === 'lname' ||
                    lowerColumn === 'surname' ||
                    (lowerColumn.includes('family') && lowerColumn.includes('name'))
                ) {
                    return column
                }
            }
            return null
        }

        const getStaffName = (staff) => {
            // Try different name field combinations
            if (staff['First Name'] && staff['Last Name']) {
                return `${staff['First Name']} ${staff['Last Name']}`
            }
            if (staff['FirstName'] && staff['LastName']) {
                return `${staff['FirstName']} ${staff['LastName']}`
            }
            if (staff['Name']) {
                return staff['Name']
            }
            if (staff['Full Name']) {
                return staff['Full Name']
            }
            // Try to find any field that might contain a name
            for (const key of Object.keys(staff)) {
                if (key.toLowerCase().includes('name') && !key.toLowerCase().includes('user') && staff[key]) {
                    return staff[key]
                }
            }
            return 'Unknown Staff'
        }

        const emailColumn = findEmailColumn(staffData[0])
        const firstNameColumn = findFirstNameColumn(staffData[0])
        const lastNameColumn = findLastNameColumn(staffData[0])

        // Check for required columns
        if (!emailColumn) {
            issues.push({ type: 'error', message: 'No email column found in staff data. Staff must have email addresses.' })
        }
        if (!firstNameColumn) {
            issues.push({ type: 'error', message: 'No first name column found in staff data. Staff must have first names.' })
        }
        if (!lastNameColumn) {
            issues.push({ type: 'error', message: 'No last name column found in staff data. Staff must have last names.' })
        }

        if (!emailColumn || !firstNameColumn || !lastNameColumn) {
            return {
                isValid: false,
                issues,
                summary: {
                    total: staffData.length,
                    validRecords: 0,
                    missingEmails: emailColumn ? 0 : staffData.length,
                    missingFirstNames: firstNameColumn ? 0 : staffData.length,
                    missingLastNames: lastNameColumn ? 0 : staffData.length,
                    duplicateEmails: 0,
                },
            }
        }

        // Validate each staff member
        staffData.forEach((staff, index) => {
            const email = staff[emailColumn] ? String(staff[emailColumn]).trim().toLowerCase() : ''
            const firstName = staff[firstNameColumn] ? String(staff[firstNameColumn]).trim() : ''
            const lastName = staff[lastNameColumn] ? String(staff[lastNameColumn]).trim() : ''
            const staffName = getStaffName(staff)
            let hasValidationIssue = false

            // Check for missing email
            if (!email) {
                missingEmails++
                issues.push({
                    type: 'error',
                    message: `Staff member ${staffName} (row ${index + 1}) is missing email address`,
                    staff,
                    index,
                })
                hasValidationIssue = true
            } else {
                // Validate email format
                if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                    issues.push({
                        type: 'warning',
                        message: `Staff member ${staffName} has invalid email format: ${email}`,
                        staff,
                        index,
                    })
                }

                // Check for duplicate emails
                if (emails.has(email)) {
                    const existingIndex = emails.get(email)
                    const existingStaff = staffData[existingIndex]

                    duplicateEmails++
                    if (!issues.some((issue) => issue.type === 'error' && issue.message.includes(`Duplicate email: ${email}`))) {
                        issues.push({
                            type: 'error',
                            message: `Duplicate email: ${email} - Used by ${getStaffName(existingStaff)} (row ${existingIndex + 1}) and ${staffName} (row ${index + 1})`,
                            staff,
                            index,
                            duplicateWith: existingIndex,
                        })
                    }
                    hasValidationIssue = true
                } else {
                    emails.set(email, index)
                }
            }

            // Check for missing first name
            if (!firstName) {
                missingFirstNames++
                issues.push({
                    type: 'error',
                    message: `Staff member in row ${index + 1} is missing first name`,
                    staff,
                    index,
                })
                hasValidationIssue = true
            }

            // Check for missing last name
            if (!lastName) {
                missingLastNames++
                issues.push({
                    type: 'error',
                    message: `Staff member in row ${index + 1} is missing last name`,
                    staff,
                    index,
                })
                hasValidationIssue = true
            }

            // Count Valid Entries
            if (!hasValidationIssue) {
                validRecords++
            }
        })

        return {
            isValid: issues.filter((issue) => issue.type === 'error').length === 0,
            issues,
            summary: {
                total: staffData.length,
                validRecords,
                missingEmails,
                missingFirstNames,
                missingLastNames,
                duplicateEmails,
            },
            columns: {
                emailColumn,
                firstNameColumn,
                lastNameColumn,
            },
        }
    }

    // Run authority analysis when staff data changes
    useEffect(() => {
        if (excelStaffData && excelStaffData.length > 0) {
            const analysis = analyzeStaffAuthority(excelStaffData, excelStudentsData)
            setAuthorityAnalysis(analysis)

            // If there are authority issues, add them to validation errors
            if (analysis.issues && analysis.issues.length > 0) {
                const authorityValidationErrors = analysis.issues.filter((issue) => issue.type === 'error').map((issue) => issue.message)

                if (authorityValidationErrors.length > 0) {
                    console.log('Authority validation errors found:', authorityValidationErrors)
                    setValidationErrors((prev) => [...prev, ...authorityValidationErrors])
                }
            }
        } else {
            setAuthorityAnalysis(null)
        }
        if (excelStaffData.length > 0 && excelStudentsData.length > 0) {
            // After processing, you can do further validation, class tree, etc.
            if (excelStudentsData.length > 0) {
                const tree = organizeClassTree(excelStudentsData)
                setClassTree(tree)
            }
            // Validate the data
            const errors = validateData(excelStudentsData || [], excelStaffData || [])
            console.log('🚀 Errors found:', errors)
            setValidationErrors((prev) => [...prev, ...errors])
        }
    }, [excelStaffData, excelStudentsData])

    // Smooth scroll to top function
    const scrollToTop = () => {
        if (containerRef.current) {
            containerRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            })
        } else {
            // Fallback for window scroll
            window.scrollTo({
                top: 0,
                behavior: 'smooth',
            })
        }
    }

    const validateData = (studentsData, staffData) => {
        const errors = []

        if (studentsData.length === 0 && staffData.length === 0) {
            errors.push('No student or staff data found in the Excel file.')
        }

        // Helper function to check if a column exists with flexible matching
        const hasColumn = (data, patterns) => {
            if (!data || data.length === 0) return false

            const headers = Object.keys(data[0]).map((h) => h.toLowerCase())
            return patterns.some((pattern) => headers.some((header) => header.includes(pattern)))
        }

        // Helper to check if students have primary access column
        const hasEarlyAccessColumn = (data) => {
            if (!data || data.length === 0) return false
            const headers = Object.keys(data[0]).map((h) => h.toLowerCase())
            return headers.some((header) => header.includes('upstrive early access') || header.includes('early access'))
        }

        // Validate student data
        if (studentsData.length > 0) {
            // Check for required columns with flexible matching
            const requiredColumnPatterns = [
                ['id', 'number', 'student id', 'pupil id'],
                ['first name', 'firstname', 'last name', 'lastname', 'name', 'full name'],
            ]

            const missingColumns = requiredColumnPatterns
                .filter((patterns) => !hasColumn(studentsData, patterns))
                .map((patterns) => patterns[0].charAt(0).toUpperCase() + patterns[0].slice(1))

            if (missingColumns.length > 0) {
                errors.push(`Missing required columns for students: ${missingColumns.join(', ')}`)
            }

            // Check for class/year information (same detection the processor and preview use)
            const { classColumn, yearColumn } = detectStudentColumns(studentsData)
            if (!classColumn && !yearColumn) {
                errors.push('No class or year information found for students.')
            }

            // Check for early access column
            const hasEarlyAccess = hasEarlyAccessColumn(studentsData)

            // If early access column exists, email is required for all students who are not marked as primary
            // If no early access column exists, treat all as secondary and require email for all
            const hasEmailColumn = hasColumn(studentsData, ['email', 'mail', 'username', 'user name', 'user'])

            if (!hasEmailColumn) {
                if (hasEarlyAccess) {
                    errors.push('Email column is required for secondary students (students without primary access).')
                } else {
                    errors.push('Email column is required for all students (no primary access column found).')
                }
            }
        }

        // Validate staff data (unchanged)
        if (staffData.length > 0) {
            const requiredColumnPatterns = [
                ['id', 'number', 'staff id', 'teacher id'],
                ['first name', 'firstname', 'last name', 'lastname'],
                ['email', 'mail', 'username', 'user name', 'user'],
            ]

            const missingColumns = requiredColumnPatterns
                .filter((patterns) => !hasColumn(staffData, patterns))
                .map((patterns) => patterns[0].charAt(0).toUpperCase() + patterns[0].slice(1))

            if (missingColumns.length > 0) {
                errors.push(`Missing required columns for staff: ${missingColumns.join(', ')}`)
            }

            const hasAuthorityColumn = hasColumn(staffData, ['authority', 'role', 'access', 'permission', 'privilege'])
            if (!hasAuthorityColumn) {
                errors.push('No authority/role column found for staff members.')
            }
        }

        return errors
    }

    // Reset all state to initial values
    const resetApplication = () => {
        setStudentAnalysis(null)
        setStudentsData([])
        setStaffData([])
        setSheetNames([])
        setError(null)
        setValidationErrors([])
        setActiveTab('students')
        setIsSubmitting(false)
        setSubmitSuccess(null)
        setSubmitError(null)
        setClassTree({})
        setAuthorityAnalysis(null)
        setSettings({
            removePreviousData: true,
            promptNewPassword: false,
            useSheetPassword: false,
            uploadType: null,
        })
        setCurrentStep(0)
        setUploadedFile(null)
        setUploadType(null)
        setIsProcessingFile(false)
        setProcessingProgress(0)
        setPdfFile(null)
        setIsUploadingPdf(false)
        setPdfUploadError(null)
        setUploadedPdfPath(null)
        resetProcessExcel()
    }

    const handleFileProcessing = async (file) => {
        // Don't reset everything, just clear previous data
        handlePdfFileSelection(file)
        setStudentAnalysis(null)
        setStudentsData([])
        setStaffData([])
        setSheetNames([])
        setError(null)
        setValidationErrors([])
        setClassTree({})
        setAuthorityAnalysis(null)
        setSubmitSuccess(null)
        setSubmitError(null)

        setUploadedFile(file)
        setIsProcessingFile(true)
        setProcessingProgress(0)

        // More realistic progress simulation
        const progressInterval = setInterval(() => {
            setProcessingProgress((prev) => {
                const increment = Math.random() * 10 + 2 // 2-12% increments
                const newProgress = Math.min(prev + increment, 85)

                if (newProgress >= 85) {
                    clearInterval(progressInterval)
                }

                return newProgress
            })
        }, 600) // Slower updates

        try {
            await processFile(file)
            clearInterval(progressInterval)
            // Complete progress
            // setProcessingProgress(100);
            setTimeout(() => {
                setIsProcessingFile(false)
                setProcessingProgress(0)
            }, 1000)
        } catch (error) {
            clearInterval(progressInterval)
            setIsProcessingFile(false)
            setProcessingProgress(0)
        }
    }

    const handleFileRemoved = () => {
        // Only reset file-related state, don't go back to step 0
        setStudentAnalysis(null)
        setStudentsData([])
        setStaffData([])
        setSheetNames([])
        setError(null)
        setValidationErrors([])
        setClassTree({})
        setAuthorityAnalysis(null)
        setUploadedFile(null)
        setIsProcessingFile(false)
        setProcessingProgress(0)
        resetProcessExcel()
    }

    // Stores the workbook so it can be opened later from Previous Uploads. The
    // rows are parsed in the browser, so a failure here never blocked the import
    // and nothing surfaced it: uploadFileToServer resolves with a status instead
    // of throwing, so a non-200 fell through the `if` into an empty catch and the
    // run finished with file: null. Three imports were saved that way with no
    // retrievable workbook. Submitting is now gated on this succeeding.
    const handlePdfFileSelection = async (file) => {
        setPdfFile(file)
        setPdfUploadError(null)
        setUploadedPdfPath(null)

        // Start uploading immediately in the background
        setIsUploadingPdf(true)

        try {
            const response = await uploadFileToServer(file, 'pdf')
            if (response.status === 200 && response.data?.storage_name) {
                setUploadedPdfPath(response.data)
            } else {
                setPdfUploadError(t('Could not store the Excel file. Please retry the upload before continuing.'))
            }
        } catch (error) {
            setPdfUploadError(`${t('Could not store the Excel file. Please retry the upload before continuing.')} (${error.message})`)
            console.error('Excel file upload error:', error)
        } finally {
            setIsUploadingPdf(false)
        }
    }

    const retryFileUpload = () => {
        if (uploadedFile) handlePdfFileSelection(uploadedFile)
    }

    const handleRemovePdf = () => {
        setPdfFile(null)
        setUploadedPdfPath(null)
        setPdfUploadError(null)
        setIsUploadingPdf(false)
    }

    const handleSubmitData = async () => {
        setIsSubmitting(true)
        setSubmitSuccess(null)
        setSubmitError(null)

        try {
            // Use your existing authority analysis to categorize staff
            const categorizedStaff = {
                admins: authorityAnalysis?.categorized?.fullAccess || [],
                teachers: authorityAnalysis?.categorized?.teachers || [],
                headOfYear: authorityAnalysis?.categorized?.headOfYear || [],
                headOfKeyStage: authorityAnalysis?.categorized?.headOfKeyStage || [],
                unassigned: [...(authorityAnalysis?.categorized?.blank || []), ...(authorityAnalysis?.categorized?.unknown || [])],
            }

            // Categorize students
            const categorizedStudents = {
                primary: studentsData.filter((s) => s.primary || s.isPrimary),
                secondary: studentsData.filter((s) => !s.primary && !s.isPrimary),
            }

            const payload = {
                studentsData: categorizedStudents,
                staffData: categorizedStaff,
                classTree: classTree,
                settings: {
                    removePreviousData: settings.removePreviousData,
                    useSheetPassword: settings.useSheetPassword,
                    savePasswordsToMasterList: settings.savePasswordsToMasterList,
                    promptNewPassword: settings.promptNewPassword,
                },
                uploadType: uploadType,
                file: uploadedPdfPath,
            }

            const resp = await sendDataForUploading(payload)

            if (resp.status === 200) {
                setSubmitSuccess(`🎉 Your data has been imported`)
            }

            // if (resp.status == 200) {
            // 	// Start polling for status
            // 	pollImportStatus(resp.data.task_id);
            // }

            // Reset application after successful upload
            // setTimeout(() => {
            // 	resetApplication();
            // }, 30000);
        } catch (err) {
            console.error('Error submitting data:', err)
            setSubmitError(`Failed to submit data: ${err.message}`)
        } finally {
            setIsSubmitting(false)
        }
    }

    const pollImportStatus = async (taskId) => {
        const pollInterval = setInterval(async () => {
            try {
                const statusResult = await getImportStatus(taskId)

                if (statusResult.success) {
                    const task = statusResult.task

                    // Update progress display
                    // setProgress(task.progress);

                    if (task.status === 'completed') {
                        clearInterval(pollInterval)
                        setIsImporting(false)
                        setSubmitSuccess(`Import completed successfully! Processed ${task.results.students_processed} students and ${task.results.staff_processed} staff members.`)

                        // Reset application after successful upload
                        setTimeout(() => {
                            resetApplication()
                        }, 3000)
                    } else if (task.status === 'failed') {
                        clearInterval(pollInterval)
                        setIsImporting(false)
                        setSubmitError(`Import failed: ${task.message}`)
                    }

                    // Handle warnings and errors
                    if (task.errors.length > 0) {
                        setValidationErrors(task.errors)
                    }
                }
            } catch (error) {
                console.error('Error polling status:', error)
                clearInterval(pollInterval)
                setIsImporting(false)
                setSubmitError('Failed to get import status')
            }
        }, 2000) // Poll every 2 seconds
    }

    const hasData = (excelStudentsData.length > 0 || excelStaffData.length > 0) && !!uploadedFile

    // Step navigation handlers with scroll to top
    const goToStep = (step) => {
        if (step === 4 && validationErrors.length > 0) {
            return
        }

        history.push({ pathname: location.pathname, search: `?step=${setSearchFromStep(step)}` })
        setCurrentStep(step)
        // Scroll to top after a brief delay to ensure state update
        setTimeout(scrollToTop, 0)
    }

    useEffect(() => {
        const urlStep = getStepFromSearch(location.search)
        if (urlStep !== currentStep) {
            setCurrentStep(urlStep)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search])

    const nextStep = () => {
        if (currentStep < 4) {
            goToStep(currentStep + 1)
        }
    }

    const goToPrevStep = () => {
        if (currentStep > 0) {
            goToStep(currentStep - 1)
        }
    }

    const renderUploadTypeStep = () => {
        return (
            <div>
                <UploadTypeSelection uploadType={uploadType} onUploadTypeChange={setUploadType} />
                <div className="mt-4">
                    <TemplateSection />
                </div>
                <div className="mt-[56px] flex justify-between items-center">
                    {/* {renderSupportButton()} */}
                    <div></div>

                    <button
                        onClick={nextStep}
                        disabled={!uploadType}
                        className={`h-[55px] px-8 flex items-center justify-center rounded-full font-semibold text-[16px] leading-5  transition-all duration-300 ${
                            uploadType ? 'bg-deepPurple text-highlightColor hover:bg-highlightColor hover:text-deepPurple   ' : 'bg-[#BEBECE] text-white cursor-not-allowed'
                        }`}
                    >
                        {uploadType ? (
                            <span className="flex items-center space-x-2">
                                <span>{t('Continue to File Upload')}</span>
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                </svg>
                            </span>
                        ) : (
                            t('Select an upload method to continue')
                        )}
                    </button>
                </div>
            </div>
        )
    }

    // Render step 1: File Upload
    const renderFileUploadStep = () => {
        return (
            <div>
                <FileUploadArea
                    onFileSelected={handleFileProcessing}
                    uploadedFile={uploadedFile}
                    onFileRemoved={handleFileRemoved}
                    isProcessing={isProcessingFile}
                    processingProgress={processingProgress}
                />
                <ProcessingStatus
                    isLoading={excelIsLoading || isProcessingFile}
                    error={excelError}
                    validationErrors={validationErrors}
                    sheetNames={excelSheetNames}
                    warnings={studentAnalysis?.issues?.filter((e) => e.type == 'warning').map((e) => e.message) ?? []}
                    progress={isProcessingFile ? processingProgress : excelProgress}
                />

                {/* Show navigation only when file is processed and no errors */}
                {hasData && validationErrors.length === 0 && !isProcessingFile && (
                    <div className="mt-[31px] flex justify-between items-center ">
                        <button
                            onClick={goToPrevStep}
                            className="inline-flex items-center px-6 h-[50px] bg-[#ECEBF8] hover:bg-deepPurple   rounded-full text-deepPurple hover:text-white text-[16px] leading-5 font-semibold transition-all duration-300 "
                        >
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
                            </svg>
                            {t('Back to Upload Type')}
                        </button>

                        {renderSupportButton()}

                        <button
                            onClick={nextStep}
                            className="inline-flex items-center px-6 h-[50px] bg-deepPurple hover:bg-highlightColor  rounded-full text-highlightColor hover:text-deepPurple text-[16px] leading-5 font-semibold transition-all duration-300 "
                        >
                            <span>{t('Continue to Settings')}</span>
                            <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                            </svg>
                        </button>
                    </div>
                )}
                {!!validationErrors.length && <div>{renderSupportButton()}</div>}
            </div>
        )
    }

    const renderSettingsStep = () => {
        return (
            <div>
                <SettingsTab settings={settings} onSettingsChange={setSettings} studentsData={excelStudentsData} staffData={excelStaffData} uploadType={uploadType} />
                <div className="mt-[31px] flex justify-between items-center ">
                    <button
                        onClick={goToPrevStep}
                        className="inline-flex items-center px-6 h-[50px] bg-[#ECEBF8] hover:bg-deepPurple   rounded-full text-deepPurple hover:text-white text-[16px] leading-5 font-semibold transition-all duration-300 "
                    >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
                        </svg>
                        {t('Back to Upload')}
                    </button>

                    {renderSupportButton()}

                    <button
                        onClick={nextStep}
                        className="inline-flex items-center px-6 h-[50px] bg-deepPurple hover:bg-highlightColor  rounded-full text-highlightColor hover:text-deepPurple text-[16px] leading-5 font-semibold transition-all duration-300 "
                    >
                        <span>{t('Continue to Preview')}</span>
                        <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                    </button>
                </div>
            </div>
        )
    }

    // Updated Preview step with better UI alignment
    const renderPreviewStep = () => {
        const uploadConfig = getUploadTypeConfig(uploadType, t)

        // Count critical issues
        const criticalStudentIssues = studentAnalysis?.issues?.filter((issue) => issue.type === 'error').length || 0
        const criticalStaffIssues = authorityAnalysis?.issues?.filter((issue) => issue.type === 'error').length || 0
        const warningStudentIssues = studentAnalysis?.issues?.filter((issue) => issue.type === 'warning').length || 0
        const warningStaffIssues = authorityAnalysis?.issues?.filter((issue) => issue.type === 'warning').length || 0

        // The stored workbook is what Previous Uploads links to, so it has to be
        // in place before the import runs -- otherwise the import succeeds and
        // leaves a history entry nobody can open.
        const excelStored = !!uploadedPdfPath?.storage_name

        const canSubmit =
            validationErrors.length === 0 &&
            criticalStudentIssues === 0 &&
            criticalStaffIssues === 0 &&
            (studentAnalysis?.summary?.missingClass || 0) === 0 &&
            (studentAnalysis?.summary?.missingIds || 0) === 0 &&
            (studentAnalysis?.summary?.duplicateIds || 0) === 0 &&
            (studentAnalysis?.summary?.duplicateEmails || 0) === 0 &&
            excelStored &&
            !isUploadingPdf

        return (
            <div className="w-full">
                {/* Header */}
                <div className="space-y-3">
                    <h3 className="text-[31px] leading-[39px] tracking-tight  font-bold text-darkPurple mb-3">{t('Review and Submit Your Data')}</h3>
                    <p className="text-darkPurple text-[14px] leading-[23px]  font-normal">{t('Review the processed data before submitting to ensure everything looks correct')}</p>
                </div>

                {/* Upload Configuration Summary */}
                <div className={`${uploadConfig.backgroundColorAndBorder} rounded-[24px] p-3 mt-[42px] relative`}>
                    <div className="flex items-center space-x-3">
                        <div className={` w-[50px] h-[50px] rounded-full border border-[#585695] flex items-center justify-center`}>{uploadConfig.icon}</div>
                        <div className="flex-1 space-y-3 mr-[100px]">
                            <h4 className={`font-bold text-[16px] leading-5 text-darkPurple`}>
                                {t('Upload Method')}: {uploadConfig.title}
                            </h4>
                            <p className={`text-[16px] leading-[18px] font-medium text-deepPurple tracking-tight`}>{uploadConfig.description}</p>
                            <p className={`text-[16px] leading-[18px] font-medium text-deepPurple tracking-tight`}>{uploadConfig.details}</p>
                        </div>
                    </div>
                    <div
                        className={`absolute top-[19px] right-[20px] px-3 h-[36px] flex items-center justify-center bg-white ${uploadConfig.color} rounded-full text-[18px] leading-[18px] font-semibold tracking-tight`}
                    >
                        {t('Active')}
                    </div>
                </div>

                {/* Data Overview Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                    {/* Students Overview */}
                    <div className="bg-white rounded-[20px] border border-[#BEBECE] px-[21px] py-[23px]">
                        <div className="flex items-center justify-between ">
                            <div className="flex items-center space-x-5">
                                <div
                                    className={`w-12 h-12 rounded-full flex items-center justify-center ${
                                        settings.removePreviousData ? 'bg-[#ECEBF8] text-deepPurple' : 'bg-[#ECEBF8] text-mudPurple'
                                    }`}
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                                        />
                                    </svg>
                                </div>
                                <h4 className="text-[24px] leading-[30px] font-bold text-darkPurple">{t('Student Data')}</h4>
                            </div>

                            {(criticalStudentIssues > 0 || warningStudentIssues > 0) && (
                                <div className="flex flex-row items-center px-4 h-[30px] rounded-full bg-[#FDDF6D99] space-x-1">
                                    {criticalStudentIssues > 0 && (
                                        <span className=" text-deepPurple text-[16px] leading-[18px]  font-medium">
                                            {criticalStudentIssues} error{criticalStudentIssues !== 1 ? 's' : ''}
                                        </span>
                                    )}
                                    {warningStudentIssues > 0 && (
                                        <span className=" text-deepPurple text-[16px] leading-[18px]  font-medium">
                                            {warningStudentIssues} warning{warningStudentIssues !== 1 ? 's' : ''}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="space-y-4 mt-[27px]">
                            <div className="flex flex-row items-center justify-between ">
                                <span className="text-[16px]  font-semibold text-darkPurple">{t('Total Students')}</span>
                                <div className="px-3 h-[34px] flex items-center justify-center bg-[#F8F8FB] text-darkPurple rounded-full text-[18px]  font-semibold ">
                                    <span>{excelStudentsData.length}</span>
                                </div>
                            </div>
                            {studentAnalysis && (
                                <>
                                    <div className="flex flex-row items-center justify-between ">
                                        <span className="text-[16px]  font-semibold text-darkPurple">{t('Primary Students')}</span>
                                        <div className="px-3 h-[34px] flex items-center justify-center bg-[#F8F8FB] text-darkPurple rounded-full text-[18px]  font-semibold ">
                                            <span>{studentAnalysis.summary.primaryStudents}</span>
                                        </div>
                                    </div>
                                    <div className="flex flex-row items-center justify-between ">
                                        <span className="text-[16px]  font-semibold text-darkPurple">{t('Secondary Students')}</span>
                                        <div className="px-3 h-[34px] flex items-center justify-center bg-[#F8F8FB] text-darkPurple rounded-full text-[18px] font-semibold ">
                                            <span>{studentAnalysis.summary.secondaryStudents}</span>
                                        </div>
                                    </div>
                                    <div className="flex flex-row items-center justify-between ">
                                        <span className="text-[16px]  font-semibold text-darkPurple">{t('Valid Entries')}</span>
                                        <div className="px-3 h-[34px] flex items-center justify-center bg-[#F8F8FB] text-darkPurple rounded-full text-[18px]  font-semibold ">
                                            <span>{studentAnalysis.summary.validRecords}</span>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                    {/* Staff Overview */}
                    <div className="bg-white rounded-[20px] border border-[#BEBECE] px-[21px] py-[23px]">
                        <div className="flex items-center justify-between ">
                            <div className="flex items-center space-x-5">
                                <div
                                    className={`w-12 h-12 rounded-full flex items-center justify-center ${
                                        settings.removePreviousData ? 'bg-[#ECEBF8] text-deepPurple' : 'bg-[#ECEBF8] text-mudPurple'
                                    }`}
                                >
                                    <svg className="w-5 h-5 text-deepPurple" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m8 0V6a2 2 0 012 2v6.759"
                                        />
                                    </svg>
                                </div>
                                <h4 className="text-[24px] leading-[30px] font-bold text-darkPurple">{t('Staff Data')}</h4>
                            </div>

                            {(criticalStaffIssues > 0 || warningStaffIssues > 0) && (
                                <div className="flex flex-row items-center px-4 h-[30px] rounded-full bg-[#FDDF6D99] space-x-1">
                                    {criticalStaffIssues > 0 && (
                                        <span className=" text-deepPurple text-[16px] leading-[18px]  font-medium">
                                            {criticalStaffIssues} error{criticalStaffIssues !== 1 ? 's' : ''}
                                        </span>
                                    )}
                                    {warningStaffIssues > 0 && (
                                        <span className=" text-deepPurple text-[16px] leading-[18px]  font-medium">
                                            {warningStaffIssues} warning{warningStaffIssues !== 1 ? 's' : ''}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="space-y-4 mt-[27px]">
                            <div className="flex flex-row items-center justify-between ">
                                <span className="text-[16px]  font-semibold text-darkPurple">{t('Total Staff')}</span>
                                <div className="px-3 h-[34px] flex items-center justify-center bg-[#F8F8FB] text-darkPurple rounded-full text-[18px] font-semibold ">
                                    <span>{excelStaffData.length}</span>
                                </div>
                            </div>
                            {authorityAnalysis && (
                                <>
                                    <div className="flex flex-row items-center justify-between ">
                                        <span className="text-[16px] font-semibold text-darkPurple">{t('Teachers')}</span>
                                        <div className="px-3 h-[34px] flex items-center justify-center bg-[#F8F8FB] text-darkPurple rounded-full text-[18px] font-semibold ">
                                            <span>{authorityAnalysis.summary.teachers}</span>
                                        </div>
                                    </div>
                                    <div className="flex flex-row items-center justify-between ">
                                        <span className="text-[16px] font-semibold text-darkPurple">{t('Head of Year')}</span>
                                        <div className="px-3 h-[34px] flex items-center justify-center bg-[#F8F8FB] text-darkPurple rounded-full text-[18px] font-semibold ">
                                            <span>{authorityAnalysis.summary.headOfYear}</span>
                                        </div>
                                    </div>
                                    <div className="flex flex-row items-center justify-between ">
                                        <span className="text-[16px] font-semibold text-darkPurple">{t('Full Access')}</span>
                                        <div className="px-3 h-[34px] flex items-center justify-center bg-[#F8F8FB] text-darkPurple rounded-full text-[18px] font-semibold ">
                                            <span>{authorityAnalysis.summary.fullAccess}</span>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Status Alert */}
                {!canSubmit ? (
                    <div className="bg-[#FC4C5933]  rounded-[24px] p-3 flex-1 mt-[27px]">
                        <div className="flex items-center space-x-3">
                            <div className={`bg-[#FC4C5933] w-[50px] h-[50px] rounded-full flex items-center justify-center`}>
                                <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <g clip-path="url(#clip0_205_4169)">
                                        <path
                                            d="M1.5 3.99959V9.99959M1.5 9.99959H7.5M1.5 9.99959L6.14 5.63959C7.21475 4.5643 8.54437 3.77879 10.0048 3.35636C11.4652 2.93393 13.0089 2.88834 14.4917 3.22385C15.9745 3.55935 17.3482 4.26502 18.4845 5.27501C19.6209 6.28499 20.4828 7.56637 20.99 8.99959M23.5 19.9996V13.9996M23.5 13.9996H17.5M23.5 13.9996L18.86 18.3596C17.7853 19.4349 16.4556 20.2204 14.9952 20.6428C13.5348 21.0652 11.9911 21.1108 10.5083 20.7753C9.02547 20.4398 7.6518 19.7342 6.51547 18.7242C5.37913 17.7142 4.51717 16.4328 4.01 14.9996"
                                            stroke="#585695"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_205_4169">
                                            <rect width="24" height="24" fill="white" transform="translate(0.5)" />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </div>
                            <div>
                                <span className="text-[16px] leading-5 font-bold text-darkPurple">{t('Data Issues Must Be Resolved')}:</span>
                                <div className="text-[16px] leading-5 font-bold text-darkPurple">
                                    {t('Please review and fix the issues in the detailed tabs below before submitting your data.')}
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="bg-[#B7E6C380]  rounded-[24px] p-3 flex-1 mt-[27px]">
                        <div className="flex items-center space-x-3">
                            <div className={`bg-[#B7E6C380] w-[50px] h-[50px] rounded-full flex items-center justify-center`}>
                                <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <g clip-path="url(#clip0_205_4169)">
                                        <path
                                            d="M1.5 3.99959V9.99959M1.5 9.99959H7.5M1.5 9.99959L6.14 5.63959C7.21475 4.5643 8.54437 3.77879 10.0048 3.35636C11.4652 2.93393 13.0089 2.88834 14.4917 3.22385C15.9745 3.55935 17.3482 4.26502 18.4845 5.27501C19.6209 6.28499 20.4828 7.56637 20.99 8.99959M23.5 19.9996V13.9996M23.5 13.9996H17.5M23.5 13.9996L18.86 18.3596C17.7853 19.4349 16.4556 20.2204 14.9952 20.6428C13.5348 21.0652 11.9911 21.1108 10.5083 20.7753C9.02547 20.4398 7.6518 19.7342 6.51547 18.7242C5.37913 17.7142 4.51717 16.4328 4.01 14.9996"
                                            stroke="#585695"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_205_4169">
                                            <rect width="24" height="24" fill="white" transform="translate(0.5)" />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </div>
                            <div>
                                <span className="text-[16px] leading-5 font-bold text-darkPurple">{t('Data Ready for Import')}:</span>
                                <div className="text-[16px] leading-5 font-bold text-darkPurple">
                                    {t('All critical issues have been resolved. Your data is ready to be submitted.')}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                <div className="flex flex-1 flex-row items-center justify-between mt-14">
                    <div className="flex flex-row items-center gap-[15px]">
                        <button
                            onClick={() => setActiveTab('students')}
                            disabled={excelStudentsData.length === 0}
                            className={`relative inline-flex items-center px-6 h-[50px] rounded-full text-[18px] leading-5 font-semibold transition-all duration-100  
        ${excelStudentsData.length === 0 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        ${
            activeTab === 'students'
                ? 'bg-highlightColor text-darkPurple after:content-[""] after:absolute after:bottom-[-11px] after:left-1/2 after:-translate-x-1/2 after:w-[22px] after:h-[22px] after:bg-highlightColor after:rotate-45 after:rounded-[2px] after:transition-transform after:duration-100 after:origin-center after:scale-100'
                : 'bg-[#ECEBF8] hover:bg-deepPurple text-darkPurple hover:text-white after:scale-0 after:transition-transform after:duration-300 after:origin-center'
        }
    `}
                        >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                                />
                            </svg>
                            {t('Students')} ({excelStudentsData.length})
                            {criticalStudentIssues > 0 && (
                                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-[#FC4C5933] text-[#FC4C59]" title={t('Number of critical issues')}>
                                    {criticalStudentIssues}
                                </span>
                            )}
                            {warningStudentIssues > 0 && (
                                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-white text-deepPurple" title={t('Number of warnings')}>
                                    {warningStudentIssues}
                                </span>
                            )}
                        </button>
                        <button
                            onClick={() => setActiveTab('staff')}
                            disabled={excelStaffData.length === 0}
                            className={`relative inline-flex items-center px-6 h-[50px] rounded-full text-[18px] leading-5 font-semibold transition-all duration-300 
        ${excelStaffData.length === 0 ? 'opacity-50 cursor-not-allowed hover:!bg-[#ECEBF8] hover:!text-darkPurple' : 'cursor-pointer'}
        ${
            activeTab === 'staff'
                ? 'bg-highlightColor text-darkPurple after:content-[""] after:absolute after:bottom-[-11px] after:left-1/2 after:-translate-x-1/2 after:w-[22px] after:h-[22px] after:bg-highlightColor after:rotate-45 after:rounded-[2px] after:origin-center after:scale-100 after:opacity-100 after:transition-all after:duration-300'
                : 'bg-[#ECEBF8] hover:bg-deepPurple text-darkPurple hover:text-white after:scale-0 after:opacity-0 after:origin-center after:transition-all after:duration-300'
        }
    `}
                        >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m8 0V6a2 2 0 012 2v6.759"
                                />
                            </svg>
                            {t('staff')} ({excelStaffData.length})
                            {criticalStaffIssues > 0 && <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-white text-deepPurple">{criticalStaffIssues}</span>}
                            {warningStaffIssues > 0 && <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-white text-deepPurple">{warningStaffIssues}</span>}
                        </button>
                    </div>
                    <h4 className="text-[24px] leading-[30px] font-bold text-darkPurple">{activeTab === 'students' ? t('Student Data') : t('Staff Data')}</h4>
                </div>

                {/* Detailed Data Tabs */}

                {/* Tab Content */}
                <div className="mt-8">
                    {activeTab === 'students' ? (
                        <StudentAnalysis studentsData={excelStudentsData} studentAnalysis={studentAnalysis} currentClasses={currentClasses} settings={settings} />
                    ) : (
                        <AuthorityAnalysis staffData={excelStaffData} authorityAnalysis={authorityAnalysis} currentStaff={currentStaff} settings={settings} />
                    )}
                </div>

                {/* Success/Error Messages */}
                {submitSuccess && (
                    <div className="bg-[#B7E6C380] rounded-xl py-9  text-center animate-in slide-in-from-bottom-4 duration-500 mt-8">
                        <div className="w-[50px] h-[50px] bg-containerGray rounded-full flex items-center justify-center mx-auto">
                            <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M10.0516 17.9996L4.35156 12.2996L5.77656 10.8746L10.0516 15.1496L19.2266 5.97461L20.6516 7.39961L10.0516 17.9996Z" fill="#2F8946" />
                            </svg>
                        </div>
                        <h3 className="text-[24px] leading-5 font-bold text-[#2F8946] mt-4">{t('Upload Successful!')}</h3>
                        <p className="text-[16px] leading-[18px] font-medium text-[#2F8946] mt-[22px]">{submitSuccess}</p>
                    </div>
                )}

                {submitError && (
                    <div className=" bg-[#FC4C5933] border border-[#FC4C59] rounded-xl p-4 mt-8">
                        <div className="flex items-start space-x-3">
                            <svg className="w-6 h-6 text-[#FC4C59] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z"
                                />
                            </svg>
                            <div>
                                <h5 className="text-[#FC4C59] font-medium mb-1">{t('Import Failed')}</h5>
                                <p className="text-[#FC4C59] text-sm">{submitError}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Stored-workbook state. Import is blocked until this succeeds, so the
                    reason has to be on screen rather than only in the console. */}
                {isUploadingPdf && (
                    <div className="bg-[#ECEBF8] border border-deepPurple rounded-xl p-4 mt-8">
                        <p className="text-deepPurple text-sm font-medium">{t('Storing the Excel file, please wait...')}</p>
                    </div>
                )}

                {!isUploadingPdf && pdfUploadError && (
                    <div className="bg-[#FC4C5933] border border-[#FC4C59] rounded-xl p-4 mt-8">
                        <div className="flex items-start justify-between space-x-3">
                            <div>
                                <h5 className="text-[#FC4C59] font-medium mb-1">{t('Excel File Not Stored')}</h5>
                                <p className="text-[#FC4C59] text-sm">{pdfUploadError}</p>
                            </div>
                            <button
                                onClick={retryFileUpload}
                                className="shrink-0 px-4 h-[38px] bg-deepPurple hover:bg-highlightColor rounded-full text-highlightColor hover:text-deepPurple text-[14px] font-semibold transition-all duration-300"
                            >
                                {t('Retry Upload')}
                            </button>
                        </div>
                    </div>
                )}

                {/* Navigation */}

                <div className="mt-[31px] flex justify-between items-center ">
                    <button
                        onClick={goToPrevStep}
                        className="inline-flex items-center px-6 h-[50px] bg-[#ECEBF8] hover:bg-deepPurple   rounded-full text-deepPurple hover:text-white text-[16px] leading-5 font-semibold transition-all duration-300 "
                    >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
                        </svg>
                        {t('Back to Settings')}
                    </button>

                    {renderSupportButton()}

                    <button
                        onClick={handleSubmitData}
                        disabled={isSubmitting || !canSubmit}
                        className={`inline-flex items-center px-8 h-[50px] bg-deepPurple hover:bg-highlightColor  rounded-full text-highlightColor hover:text-deepPurple text-[16px] leading-5 font-semibold transition-all duration-300 ${
                            isSubmitting || !canSubmit ? 'cursor-not-allowed' : ''
                        }`}
                    >
                        {isSubmitting ? (
                            <>
                                <svg className="animate-spin-slow-clockwise -ml-1 mr-3 h-5 w-5 " xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    ></path>
                                </svg>
                                {uploadType === 'full_update' ? t('Updating School Data...') : t('Adding New Users...')}
                            </>
                        ) : (
                            <>
                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                    />
                                </svg>
                                {uploadType === 'full_update' ? t('Update School Data') : t('Add New Users')}
                            </>
                        )}
                    </button>
                </div>
            </div>
        )
    }

    const renderStepsIndicator = () => {
        const steps = [
            {
                number: 1,
                label: t('Upload Type'),
                shortLabel: t('Type'),
                active: currentStep >= 0,
                completed: currentStep > 0,
                clickable: true,
                icon: (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                        />
                    </svg>
                ),
            },
            {
                number: 2,
                label: t('Upload File'),
                shortLabel: t('Upload'),
                active: currentStep >= 1,
                completed: currentStep > 1,
                clickable: currentStep > 0 && uploadType, // Can go back if upload type is selected
                icon: (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                        />
                    </svg>
                ),
            },
            {
                number: 3,
                label: t('Settings'),
                shortLabel: t('Settings'),
                active: currentStep >= 2,
                completed: currentStep > 2,
                clickable: currentStep > 1 && hasData && validationErrors.length === 0, // Can go back if file is uploaded successfully
                icon: (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                        />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                ),
            },
            {
                number: 4,
                label: t('Preview & Submit'),
                shortLabel: t('Preview'),
                active: currentStep >= 3,
                completed: submitSuccess !== null,
                clickable: currentStep > 2, // Can go back if settings are configured
                icon: (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                ),
            },
        ]

        const handleStepClick = (stepIndex) => {
            const step = steps[stepIndex]
            if (step.clickable && stepIndex <= currentStep) {
                goToStep(stepIndex)
            }
        }

        const getStepStatus = (step, index) => {
            if (step.completed) return 'completed'
            if (currentStep === index) return 'current'
            if (step.active) return 'active'
            return 'inactive'
        }

        const getStepClasses = (step, index) => {
            const status = getStepStatus(step, index)
            const isClickable = step.clickable && index <= currentStep

            const baseClasses = 'relative flex items-center transition-all duration-300'
            const cursorClasses = isClickable ? 'cursor-pointer group' : 'cursor-default'

            return `${baseClasses} ${cursorClasses}`
        }

        return (
            <div className="mb-[42px] flex items-center justify-center w-full  ">
                <div className="bg-containerGray  w-full max-w-4xl flex items-center justify-center py-[29px] px-6 rounded-[32px] shadow-[0px_12px_30px_rgba(45,43,69,0.02)]">
                    <nav className="flex flex-row items-center w-full " aria-label="Progress">
                        {steps.map((step, index) => {
                            const status = getStepStatus(step, index)
                            const isClickable = step.clickable && index <= currentStep

                            return (
                                <div key={index} className={`flex items-center flex-1 ${index === steps.length - 1 ? 'max-w-fit' : ''}`}>
                                    {/* Step Circle and Content */}
                                    <div
                                        className={getStepClasses(step, index)}
                                        onClick={() => handleStepClick(index)}
                                        role={isClickable ? 'button' : undefined}
                                        tabIndex={isClickable ? 0 : undefined}
                                        onKeyDown={(e) => {
                                            if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
                                                e.preventDefault()
                                                handleStepClick(index)
                                            }
                                        }}
                                    >
                                        {/* Step Circle */}

                                        {status === 'completed' ? (
                                            <div class="w-[50px] h-[50px] bg-[#5E639B] text-highlightColor rounded-full flex items-center justify-center box-border">
                                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                </svg>
                                            </div>
                                        ) : status === 'current' ? (
                                            <div class="w-[50px] h-[50px] border border-[#5E639B] rounded-full flex items-center justify-center box-border">
                                                <div class="w-[40px] h-[40px] border-[4px] border-[#E8DEF8] rounded-full animate-pulseSize"></div>
                                            </div>
                                        ) : (
                                            <div class="w-[50px] h-[50px] border border-[#E8DEF8] rounded-full flex items-center justify-center box-border">
                                                <div class="w-[40px] h-[40px] border-[4px] flex items-center justify-center border-[#E8DEF8] rounded-full ">
                                                    <p className="text-[#9392AD] text-[16px] leading-5 font-semibold">{step.number}</p>
                                                </div>
                                            </div>
                                        )}

                                        {/* Step Label */}
                                        <div className="ml-2 flex-1">
                                            <div
                                                className={`text-sm font-medium transition-colors duration-300 ${
                                                    status === 'completed' || status === 'current' ? 'text-gray-900' : status === 'active' ? 'text-blue-600' : 'text-gray-500'
                                                } ${isClickable ? 'group-hover:text-blue-600' : ''}`}
                                            >
                                                <span className="hidden lg:inline lg:visible text-darkPurple text-[16px] leading-5 font-bold">{step.label}</span>
                                                <span className="lg:hidden">{step.shortLabel}</span>
                                            </div>

                                            {/* Step Status */}
                                            <div
                                                className={`text-[14px] leading-5 font-medium transition-colors duration-300 mt-[5px] ${
                                                    status === 'completed' ? 'text-darkPurple' : status === 'current' ? 'text-darkPurple' : 'text-[#bebece]'
                                                }`}
                                            >
                                                {status === 'completed' && t('Completed')}
                                                {status === 'current' && t('In Progress')}
                                                {status === 'active' && status !== 'current' && t('Ready')}
                                                {status === 'inactive' && t('Pending')}
                                            </div>
                                        </div>

                                        {/* Clickable Indicator */}
                                        {/* {isClickable && index !== currentStep && (
                                            <div className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                                <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"
                                                    />
                                                </svg>
                                            </div>
                                        )} */}
                                    </div>

                                    {/* Connector Line */}
                                    {index < steps.length - 1 && <div className="flex-1 h-[1px] bg-mudPurple mx-3" />}
                                </div>
                            )
                        })}
                    </nav>
                </div>
            </div>
        )
    }

    const goBack = () => history.goBack()

    const renderSupportButton = () => (
        <div className="flex flex-col items-center mt-4 -mb-4">
            {' '}
            <button
                onClick={handleContactSupport}
                className={`inline-flex items-center px-4 py-3 rounded-full text-[14px] font-medium transition-all duration-300 shadow-sm hover:shadow-md ${
                    validationErrors.length > 0 || excelError || error || pdfUploadError
                        ? 'bg-red-50 hover:bg-red-100 border border-red-200 text-red-700 hover:text-red-800'
                        : 'bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 hover:text-gray-900'
                }`}
            >
                {' '}
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {' '}
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />{' '}
                </svg>{' '}
                {validationErrors.length > 0 || excelError || error || pdfUploadError ? t('Having Issues? Contact Support') : t('Need Help? Contact Support')}{' '}
            </button>{' '}
            <p className="text-[12px] text-gray-500 mt-2 text-center">{t('Click to send an email with your current upload details')}</p>{' '}
        </div>
    )

    const handleContactSupport = () => {
        const stepNames = ['Upload Type Selection', 'File Upload', 'Settings Configuration', 'Data Preview']
        const currentStepName = stepNames[currentStep] || 'Unknown Step'

        const fileName = uploadedFile?.name || 'No file uploaded'
        const hasStudentData = excelStudentsData.length > 0
        const hasStaffData = excelStaffData.length > 0
        const hasValidationErrors = validationErrors.length > 0
        const hasProcessingErrors = excelError || error
        const hasPdfError = pdfUploadError

        // Collect error information
        let errorDetails = ''
        if (hasValidationErrors) {
            errorDetails += `\nValidation Errors (first 5):\n${validationErrors
                .slice(0, 5)
                .map((err) => `- ${err}`)
                .join('\n')}`
            if (validationErrors.length > 5) {
                errorDetails += `\n... and ${validationErrors.length - 5} more errors`
            }
        }
        if (hasProcessingErrors) {
            errorDetails += `\nProcessing Error: ${excelError || error}`
        }
        if (hasPdfError) {
            errorDetails += `\nPDF Upload Error: ${pdfUploadError}`
        }

        const emailBody = `Dear Support Team,

I am experiencing an issue while uploading school data through the Upstrive system.

Current Details:
- Current Step: ${currentStepName} (Step ${currentStep + 1} of 4)
- Uploaded File: ${fileName}
- Upload Type: ${uploadType || 'Not selected'}
- Student Records: ${hasStudentData ? `${excelStudentsData.length} students` : 'No student data'}
- Staff Records: ${hasStaffData ? `${excelStaffData.length} staff members` : 'No staff data'}
- PDF Document: ${uploadedPdfPath ? 'Successfully attached' : pdfFile ? 'Upload in progress' : 'Not attached'}
- Validation Status: ${hasValidationErrors ? `${validationErrors.length} errors found` : 'No validation errors'}
${errorDetails}

Issue Description:
[Please describe your issue here - what were you trying to do when the problem occurred?]

Additional Information:
- Browser: ${navigator.userAgent}
- Timestamp: ${new Date().toISOString()}

I would appreciate your assistance in resolving this issue.

Best regards,
[Your Name]`

        const subject = 'Issue with uploading data'
        const mailtoLink = `mailto:support@upstrivesystem.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(emailBody)}`

        window.open(mailtoLink, '_blank')
    }

    return (
        <div ref={containerRef} className="my-[42px] mx-8">
            <div className="max-w-[1464px] mx-auto w-full self-center relative">
                <button className="flex flex-row items-center justify-center gap-[10px] bg-white rounded-full  px-[15px] h-[40px] absolute left-0" onClick={goBack}>
                    <GoNextIcon />
                    <p className="text-[16px] leading-5 text-deepPurple font-semibold">{t('back')}</p>
                </button>

                <button
                    className="flex flex-row items-center justify-center gap-[10px] bg-deepPurple hover:bg-highlightColor rounded-full px-[15px] h-[40px] absolute right-0 transition-all duration-300 group"
                    onClick={() => setShowPreviousUploadsModal(true)}
                >
                    <svg className="w-5 h-5 text-highlightColor group-hover:text-deepPurple transition-all duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                    </svg>
                    <p className="text-[16px] leading-5 text-highlightColor font-semibold group-hover:text-deepPurple transition-all duration-300">{t('Previous Uploads')}</p>
                </button>

                <h2 className="text-[31px] leading-[31px] tracking-tight font-bold mb-[32px] text-center text-darkPurple">{t('School Data Upload')}</h2>

                {renderStepsIndicator()}
            </div>
            <div className="bg-white rounded-[32px] overflow-hidden py-[52px] px-[48px] shadow-[0px_12px_30px_rgba(45,43,69,0.02)] max-w-[1464px] mx-auto">
                {currentStep === 0 && renderUploadTypeStep()}
                {currentStep === 1 && renderFileUploadStep()}
                {currentStep === 2 && renderSettingsStep()}
                {currentStep === 3 && renderPreviewStep()}
            </div>

            {/* Email Scheduling Modal */}
            <PreviousUploadsModal
                modalIsOpen={showPreviousUploadsModal}
                closeModal={() => setShowPreviousUploadsModal(false)}
                onSelectUpload={(upload) => console.log('Selected upload:', upload)}
            />
        </div>
    )
}

export default ExcelUploader
